import React, { useState, useEffect, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { 
  collection, 
  onSnapshot, 
  orderBy, 
  query, 
  serverTimestamp,
  doc,
  setDoc
} from 'firebase/firestore';
import { auth, db } from '../firebase.config.js';
import { useAgency } from '../AgencyContext.js';
import { useChatNotifications } from './useChatNotifications';

// Components
import MessagesList from './MessagesList';
import MessageInput from './MessageInput';

// Hooks & Utils
import { useChat } from './useChat';
import { formatDate, shouldShowDateDivider } from './utils/dateUtils';

// Styles
import './Chat.css';

const Chat = () => {
  const [user] = useAuthState(auth);
  const { currentAgency } = useAgency();
  const [newMessage, setNewMessage] = useState('');
  const selectedChannel = 'general'; // Fixed to general channel for MVP
  
  const messagesEndRef = useRef(null);
  const { markChannelAsRead, markMessageAsRead } = useChatNotifications();

  // Custom hook for chat functionality
  const {
    messages,
    loading,
    userReadStatus,
    typingUsers,
    isTyping,
    sendMessage,
    startTyping,
    stopTyping,
    addReaction,
    removeReaction,
    typingTimeoutRef
  } = useChat(user, currentAgency, selectedChannel);

  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('.chat-messages');
    if (messagesContainer) {
      // Force immediate scroll to absolute bottom
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  // Track previous messages length to only scroll on new messages, not reactions
  const [prevMessagesLength, setPrevMessagesLength] = useState(0);
  const [justSentMessage, setJustSentMessage] = useState(false);

  // Always ensure we're at the bottom when messages first load
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length]);

  useEffect(() => {
    // Always scroll to bottom when there are new messages
    if (messages.length > prevMessagesLength) {
      scrollToBottom();
      if (justSentMessage) {
        setJustSentMessage(false);
      }
    }
    setPrevMessagesLength(messages.length);
  }, [messages, prevMessagesLength, justSentMessage]);

  // Initialize general channel
  useEffect(() => {
    if (!currentAgency?.key) return;

    const initializeChannels = async () => {
      const generalChannelRef = doc(db, 'agencies', currentAgency.key, 'channels', 'general');
      await setDoc(generalChannelRef, {
        name: 'general',
        displayName: 'General',
        description: 'General channel for the team',
        createdAt: serverTimestamp(),
        createdBy: 'system'
      }, { merge: true });
    };

    initializeChannels();

    const channelsQuery = query(
      collection(db, 'agencies', currentAgency.key, 'channels'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(channelsQuery, (snapshot) => {
      // Channel list not needed for MVP
    });

    return () => unsubscribe();
  }, [currentAgency?.key]);

  // Mark channel as read (throttled to prevent excessive writes)
  useEffect(() => {
    if (selectedChannel && messages.length > 0) {
      const timer = setTimeout(() => {
        markChannelAsRead(selectedChannel);
      }, 3000); // Increased from 1s to 3s to reduce Firebase writes

      return () => clearTimeout(timer);
    }
  }, [selectedChannel, messages.length, markChannelAsRead]);

  // Mark messages as read when visible (optimized to reduce Firebase writes)
  useEffect(() => {
    if (!currentAgency?.key || !selectedChannel || messages.length === 0) return;

    let readMessages = new Set();
    
    const observer = new IntersectionObserver(
      (entries) => {
        const messagesToMark = [];
        
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.dataset.messageId;
            const messageUid = entry.target.dataset.messageUid;
            
            if (messageId && messageUid !== user?.uid && !readMessages.has(messageId)) {
              readMessages.add(messageId);
              messagesToMark.push(messageId);
            }
          }
        });

        // Batch mark messages with delay to prevent spam
        if (messagesToMark.length > 0) {
          const latestMessageId = messagesToMark[messagesToMark.length - 1];
          setTimeout(() => {
            markMessageAsRead(latestMessageId, selectedChannel);
          }, 1000);
        }
      },
      {
        threshold: 0.5,
        rootMargin: '0px'
      }
    );

    const messageElements = document.querySelectorAll('.message-row[data-message-id]');
    messageElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [messages, selectedChannel, currentAgency?.key, user?.uid, markMessageAsRead]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageToSend = newMessage;
    // INSTANT: Clear input immediately (WhatsApp style)
    setNewMessage('');
    
    // Mark that user just sent a message
    setJustSentMessage(true);
    
    // Send message (will appear instantly via optimistic UI)
    await sendMessage(messageToSend);
    
    // Force scroll to bottom immediately after sending
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    if (value.trim() && !isTyping) {
      startTyping();
    }
    
    if (typingTimeoutRef.current[user?.uid]) {
      clearTimeout(typingTimeoutRef.current[user?.uid]);
    }
    
    if (value.trim()) {
      typingTimeoutRef.current[user?.uid] = setTimeout(() => {
        stopTyping();
      }, 2000);
    } else {
      stopTyping();
    }
  };

  if (loading) {
    return (
      <div className="chat-layout-minimal">
        <div className="chat-loading">
          <div className="loading-spinner"></div>
          <p>Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-layout-minimal">
      <div className="chat-main">

        <MessagesList
          messages={messages}
          user={user}
          userReadStatus={userReadStatus}
          typingUsers={typingUsers}
          messagesEndRef={messagesEndRef}
          formatDate={formatDate}
          shouldShowDateDivider={shouldShowDateDivider}
          onAddReaction={addReaction}
          onRemoveReaction={removeReaction}
        />

        <MessageInput
          newMessage={newMessage}
          handleInputChange={handleInputChange}
          sendMessage={handleSendMessage}
        />
      </div>
    </div>
  );
};

export default Chat;